// Mitteilungszentrum nach Themen (01.11.2026) — die EINE Liste für die
// Dashboard-Karte und die Glocke oben rechts. Die Gliederung kommt aus der
// reinen Funktion `clusterNotifications()`; hier nur Darstellung und Aktionen.
//
// Verhalten wie bisher (T-364): Ein Klick auf eine Zeile öffnet nur (Vorgang,
// Mail, Chat …), quittiert wird per Haken. Posteingangs-Mails und Anrufe haben
// denselben runden Haken: bei der Mail heißt er „als gelesen markieren" (sie
// fällt dann heraus), beim Anruf „erledigt". Keine Extra-Knöpfe in der Zeile.
// Auf/Zu je Gruppe merkt sich der Browser (localStorage, fehlertolerant).
// Lange Abschnitte wachsen nur schrittweise über „weitere anzeigen" (keine
// Seitenzahlen, kein „alle"), damit nie unabsichtlich zu viel aufgeht.
// Mails: Öffnen markiert die Mail als gelesen; bei einer Vorgangs-Mail ist
// die Mitteilung damit auch erledigt. Anruf: Klick öffnet das Anruf-Detail.
// Termin-Mails stehen im Posteingang als eigene Zeilenart mit Zusagen/Vorbehalt/Absagen
// (MzTerminZeile); beantwortet = gelöscht (Papierkorb, wie Outlook).
import * as React from "react";
import {
    Call20Regular,
    Checkmark16Regular,
    ChevronRight16Regular,
    MailInbox20Regular,
} from "@fluentui/react-icons";
import { useNotifications } from "../../context/NotificationContext";
import { useNavBadgesShared } from "../../context/NavBadgesContext";
import { useMailContext } from "../../context/MailContext";
import { ANRUFE_GEAENDERT, telephonyService } from "../../services/telephonyService";
import { graphMailService } from "../../services/graphMailService";
import { loescheMailZeile } from "../mail/mailRowActions";
import type { NotificationDto } from "../../services/notificationService";
import { hinweisen } from "../redesign/bestaetigen";
import { notifIcon, notifLabel } from "./notifIcon";
import { NotifAbhaken } from "./NotifAbhaken";
import {
    abhakbareIds,
    clusterNotifications,
    gruppiereVerpassteAnrufe,
    mitteilungsAbschnitt,
    mitteilungsZweiteZeile,
    tagVon,
    vorgangsMailTeile,
    type FristStufe,
    type GruppeKey,
    type MzAbschnitt,
    type MzEintrag,
    type MzGruppe,
    type PosteingangMail,
    type VerpassterAnruf,
} from "./clusterNotifications";
import { usePosteingangUngelesen } from "./usePosteingangUngelesen";
import { MzTerminZeile } from "./MzTerminZeile";
import "../redesign/contactRedesign.css";
import "./mitteilungszentrum.css";

/** So viele Einträge zeigt ein langer Abschnitt, bevor „weitere anzeigen" kommt. */
export const MZ_KURZ = 5;
/** Posteingang: so viele zu Beginn — und so viele kommt je „weitere anzeigen" dazu. */
export const MZ_SEITE = 10;
const OFFEN_KEY = "kmvs.mitteilungszentrum.offen.v1";

/* ────────── Zeit & Hilfen ────────── */

const zwei = (n: number) => String(n).padStart(2, "0");

/** „gerade eben" · „10 Min" · „09:12" (heute) · „gestern" · „28.10.". */
export function kurzZeit(iso: string, jetzt: Date = new Date()): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const min = Math.round((jetzt.getTime() - d.getTime()) / 60000);
    if (min < 1) return "gerade eben";
    if (min < 60) return `${min} Min`;
    if (tagVon(d) === tagVon(jetzt)) return `${zwei(d.getHours())}:${zwei(d.getMinutes())}`;
    const gestern = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate() - 1);
    if (tagVon(d) === tagVon(gestern)) return "gestern";
    return `${zwei(d.getDate())}.${zwei(d.getMonth() + 1)}.`;
}

function anrufZeit(iso: string, jetzt: Date): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const uhr = `${zwei(d.getHours())}:${zwei(d.getMinutes())}`;
    if (tagVon(d) === tagVon(jetzt)) return `heute ${uhr}`;
    const gestern = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate() - 1);
    if (tagVon(d) === tagVon(gestern)) return `gestern ${uhr}`;
    return `${zwei(d.getDate())}.${zwei(d.getMonth() + 1)}. ${uhr}`;
}

/** Eindeutig je Postfach — dieselbe Graph-Id kann in zwei Postfächern vorkommen. */
const mailSchluessel = (m: Pick<PosteingangMail, "mailbox" | "id">) => `${m.mailbox}|${m.id}`;

export const mailDeepLink = (m: Pick<PosteingangMail, "mailbox" | "id">) =>
    `/emails?mailbox=${encodeURIComponent(m.mailbox)}&message=${encodeURIComponent(m.id)}`;

/** Telefonie, Reiter „Verpasst", mit offenem Detail des jüngsten Versuchs. */
export const anrufDetailLink = (a: Pick<VerpassterAnruf, "callIds">) =>
    a.callIds.length ? `/anrufe?tab=verpasst&call=${a.callIds[0]}` : "/anrufe?tab=verpasst";

function leseOffen(): Partial<Record<GruppeKey, boolean>> {
    try {
        const roh = window.localStorage?.getItem(OFFEN_KEY);
        const v = roh ? JSON.parse(roh) : null;
        return v && typeof v === "object" ? v : {};
    } catch {
        return {};
    }
}
function schreibeOffen(v: Partial<Record<GruppeKey, boolean>>) {
    try {
        window.localStorage?.setItem(OFFEN_KEY, JSON.stringify(v));
    } catch {
        /* privat/gesperrt — dann eben nicht gemerkt */
    }
}

/** Tastatur: Enter/Leertaste lösen die Zeile aus wie ein Klick. */
const tasteWieKlick = (fn: () => void) => (e: React.KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        fn();
    }
};

const STUFE_TEXT: Record<FristStufe, string> = {
    ueberfaellig: "überfällig",
    heute: "heute",
    woche: "diese Woche",
    spaeter: "später",
};

/* ────────── Komponente ────────── */

export type MitteilungszentrumListeProps = {
    variante: "karte" | "glocke";
    /** Live-Quellen laden (Posteingang); bei geschlossener Glocke `false`. */
    aktiv?: boolean;
    /** Navigation (Glocke: schließt dabei das Popover). */
    onOeffnen: (link: string) => void;
    /** Phone: größere Trefferflächen. */
    touch?: boolean;
    /** Fester Zeitpunkt (Tests). */
    jetzt?: Date;
};

export const MitteilungszentrumListe: React.FC<MitteilungszentrumListeProps> = ({
    variante,
    aktiv = true,
    onOeffnen,
    touch = false,
    jetzt: jetztProp,
}) => {
    const { notifications, markRead, markManyRead } = useNotifications();
    const badges = useNavBadgesShared();
    const { refreshFolders } = useMailContext();
    const posteingangGeladen = usePosteingangUngelesen(aktiv);
    const [erledigtLokal, setErledigtLokal] = React.useState<ReadonlySet<string>>(new Set());
    const [gelesenLokal, setGelesenLokal] = React.useState<ReadonlySet<string>>(new Set());
    const posteingang = React.useMemo(
        () => posteingangGeladen?.filter((m) => !gelesenLokal.has(mailSchluessel(m))) ?? null,
        [posteingangGeladen, gelesenLokal],
    );
    const jetzt = jetztProp ?? new Date();

    const anrufe = React.useMemo(
        () => gruppiereVerpassteAnrufe(badges.verpassteAnrufe ?? []).filter((a) => !erledigtLokal.has(a.key)),
        [badges.verpassteAnrufe, erledigtLokal],
    );
    const cluster = React.useMemo(
        () => clusterNotifications({ notifications, posteingang: posteingang ?? [], anrufe, jetzt }),
        // `jetzt` bewusst nicht: die Stufen ändern sich höchstens um Mitternacht.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [notifications, posteingang, anrufe],
    );

    const [offen, setOffen] = React.useState<Partial<Record<GruppeKey, boolean>>>(() => leseOffen());
    const istOffen = (g: MzGruppe) => offen[g.key] ?? g.standardOffen;
    const umschalten = (g: MzGruppe) => {
        setOffen((alt) => {
            const neu = { ...alt, [g.key]: !(alt[g.key] ?? g.standardOffen) };
            schreibeOffen(neu);
            return neu;
        });
    };

    const anrufErledigen = React.useCallback(async (a: VerpassterAnruf) => {
        try {
            const ergebnisse = await Promise.allSettled(a.callIds.map((id) => telephonyService.resolveCall(id)));
            const fehler = ergebnisse.filter((e) => e.status === "rejected").length;
            if (fehler < a.callIds.length) setErledigtLokal((s) => new Set(s).add(a.key));
            if (fehler) {
                await hinweisen({
                    titel: "Nicht alles erledigt",
                    text:
                        fehler === a.callIds.length
                            ? "Der Anruf ließ sich nicht als erledigt markieren. Bitte später noch einmal versuchen oder in der Telefonie erledigen."
                            : `${fehler} von ${a.callIds.length} Anrufen ließen sich nicht als erledigt markieren.`,
                });
            }
        } finally {
            window.dispatchEvent(new Event(ANRUFE_GEAENDERT));
        }
    }, []);

    // Haken an einer Posteingangs-Mail = als gelesen markieren. Sofort lokal
    // ausblenden; die Live-Zahl zieht über den Ordnerbaum nach.
    const mailGelesen = React.useCallback(
        async (m: PosteingangMail) => {
            const key = mailSchluessel(m);
            setGelesenLokal((s) => new Set(s).add(key));
            try {
                await graphMailService.markRead(m.id, m.mailbox);
                void refreshFolders(m.mailbox);
            } catch {
                setGelesenLokal((s) => {
                    const neu = new Set(s);
                    neu.delete(key);
                    return neu;
                });
                await hinweisen({
                    titel: "Nicht als gelesen markiert",
                    text: "Die Mail ließ sich nicht als gelesen markieren. Bitte später noch einmal versuchen oder im Postfach öffnen.",
                });
            }
        },
        [refreshFolders],
    );

    // Geöffnet = gelesen. Still im Hintergrund: Das Mail-Fenster zeigt die Mail
    // ohnehin; ein Fehler hier soll das Öffnen nicht mit einer Meldung stören.
    const mailGeoeffnet = React.useCallback(
        (m: PosteingangMail) => {
            setGelesenLokal((s) => new Set(s).add(mailSchluessel(m)));
            void graphMailService
                .markRead(m.id, m.mailbox)
                .then(() => refreshFolders(m.mailbox))
                .catch(() => undefined);
        },
        [refreshFolders],
    );

    // Einladung beantwortet: wie Outlook in „Gelöschte Elemente" verschieben
    // (derselbe Weg wie „Löschen" im Postfach, MailLinks ziehen mit). Ein 404
    // heißt „schon weg" — auch gut. Scheitert das Löschen, ist die Antwort
    // trotzdem raus: dann wenigstens gelesen und Bescheid geben.
    const einladungBeantwortet = React.useCallback(
        async (m: PosteingangMail) => {
            setGelesenLokal((s) => new Set(s).add(mailSchluessel(m)));
            try {
                await loescheMailZeile(
                    { mailbox: m.mailbox, id: m.id, entwurf: false },
                    { moveMessage: graphMailService.moveMessage, deleteDraft: graphMailService.deleteDraft },
                );
                void refreshFolders(m.mailbox);
            } catch (err) {
                console.error("[Mitteilungszentrum] Einladung nicht gelöscht", err);
                void graphMailService
                    .markRead(m.id, m.mailbox)
                    .then(() => refreshFolders(m.mailbox))
                    .catch(() => undefined);
                await hinweisen({
                    titel: "Antwort gesendet",
                    text: "Die Antwort ist raus, die Einladung ließ sich aber nicht löschen. Sie liegt noch im Postfach.",
                });
            }
        },
        [refreshFolders],
    );

    // Vorgangs-Mail geöffnet: Mitteilung erledigt und — wenn bekannt — die Mail
    // im Postfach gelesen.
    const vorgangsMailGeoeffnet = React.useCallback(
        (n: NotificationDto) => {
            void markRead(n.id);
            const mail = n.meta?.mail;
            if (mail?.mailbox && mail.messageId) {
                const mailbox = mail.mailbox;
                void graphMailService
                    .markRead(mail.messageId, mailbox)
                    .then(() => refreshFolders(mailbox))
                    .catch(() => undefined);
            }
        },
        [markRead, refreshFolders],
    );

    if (cluster.leer) {
        return (
            <div className={`kvp-rd mz ${variante}`}>
                <div className="mz-leer">Keine neuen Mitteilungen.</div>
            </div>
        );
    }

    return (
        <div className={`kvp-rd mz ${variante}`} data-testid="mitteilungszentrum">
            {cluster.gruppen.map((g) => {
                const auf = istOffen(g);
                const ids = abhakbareIds(g.abschnitte);
                return (
                    <section key={g.key} className={`mz-gruppe${auf ? " offen" : ""}`} data-gruppe={g.key}>
                        <div className="mz-gkopf">
                            <button
                                type="button"
                                className="mz-gtoggle"
                                aria-expanded={auf}
                                onClick={() => umschalten(g)}
                            >
                                <span className="mz-chev" aria-hidden>
                                    <ChevronRight16Regular />
                                </span>
                                <span className="mz-gtitel">{g.titel}</span>
                                <span className="mz-anzahl" aria-label={`${g.anzahl} Einträge`}>
                                    {g.anzahl}
                                </span>
                                {g.zusammenfassung && <span className="mz-gsumme">{g.zusammenfassung}</span>}
                            </button>
                            {g.abhakbar && ids.length > 0 && (
                                <button
                                    type="button"
                                    className="mz-knopf leise"
                                    onClick={() => void markManyRead(ids)}
                                    title={`Alle ${ids.length} Mitteilungen unter „${g.titel}" abhaken`}
                                >
                                    <Checkmark16Regular /> Alle abhaken
                                </button>
                            )}
                        </div>
                        {auf &&
                            g.abschnitte.map((a) => (
                                <Abschnitt
                                    key={a.key}
                                    a={a}
                                    mitKopf={g.mitAbschnittskoepfen}
                                    jetzt={jetzt}
                                    touch={touch}
                                    onOeffnen={onOeffnen}
                                    onAbhaken={(id) => void markRead(id)}
                                    onAlleAbhaken={(ids2) => void markManyRead(ids2)}
                                    onMailGelesen={(m) => void mailGelesen(m)}
                                    onMailGeoeffnet={mailGeoeffnet}
                                    onEinladungBeantwortet={(m) => void einladungBeantwortet(m)}
                                    onVorgangsMailGeoeffnet={vorgangsMailGeoeffnet}
                                    onAnrufErledigt={(an) => void anrufErledigen(an)}
                                />
                            ))}
                    </section>
                );
            })}
        </div>
    );
};

/* ────────── Abschnitt ────────── */

const Abschnitt: React.FC<{
    a: MzAbschnitt;
    mitKopf: boolean;
    jetzt: Date;
    touch: boolean;
    onOeffnen: (link: string) => void;
    onAbhaken: (id: number) => void;
    onAlleAbhaken: (ids: number[]) => void;
    onMailGelesen: (m: PosteingangMail) => void;
    onMailGeoeffnet: (m: PosteingangMail) => void;
    onEinladungBeantwortet: (m: PosteingangMail) => void;
    onVorgangsMailGeoeffnet: (n: NotificationDto) => void;
    onAnrufErledigt: (a: VerpassterAnruf) => void;
}> = ({ a, mitKopf, jetzt, touch, onOeffnen, onAbhaken, onAlleAbhaken, onMailGelesen, onMailGeoeffnet, onEinladungBeantwortet, onVorgangsMailGeoeffnet, onAnrufErledigt }) => {
    const start = a.seitenweise ? MZ_SEITE : MZ_KURZ;
    const [zahl, setZahl] = React.useState(start);
    const sichtbar = a.eintraege.slice(0, zahl);
    const rest = a.eintraege.length - sichtbar.length;
    const naechste = Math.min(MZ_SEITE, rest);
    const ids = abhakbareIds([a]);

    return (
        <div className="mz-abschnitt" data-abschnitt={a.key}>
            {mitKopf && (
                <div className="mz-akopf">
                    <span className="mz-atitel">
                        {a.titel} ({a.eintraege.length})
                    </span>
                    <span className="mz-asumme">
                        {a.zusammenfassung ? <FristSumme text={a.zusammenfassung} /> : null}
                    </span>
                    {a.abhakbar && ids.length > 1 && (
                        <button
                            type="button"
                            className="mz-knopf leise"
                            onClick={() => onAlleAbhaken(ids)}
                            title={`Alle ${ids.length} unter „${a.titel}" abhaken`}
                        >
                            Alle abhaken
                        </button>
                    )}
                </div>
            )}
            {!mitKopf && a.zusammenfassung && (
                <div className="mz-akopf">
                    <span className="mz-asumme">
                        <FristSumme text={a.zusammenfassung} />
                    </span>
                </div>
            )}
            <ul className="mz-liste">
                {sichtbar.map((e) => (
                    <li key={e.key}>
                        <Zeile
                            e={e}
                            jetzt={jetzt}
                            touch={touch}
                            onOeffnen={onOeffnen}
                            onAbhaken={onAbhaken}
                            onMailGelesen={onMailGelesen}
                            onMailGeoeffnet={onMailGeoeffnet}
                            onEinladungBeantwortet={onEinladungBeantwortet}
                            onVorgangsMailGeoeffnet={onVorgangsMailGeoeffnet}
                            onAnrufErledigt={onAnrufErledigt}
                        />
                    </li>
                ))}
            </ul>
            {(rest > 0 || zahl > start) && (
                <div className="mz-fuss">
                    {rest > 0 && (
                        <button type="button" className="mz-knopf leise" onClick={() => setZahl((z) => z + MZ_SEITE)}>
                            {naechste} weitere anzeigen{rest > naechste ? ` (noch ${rest})` : ""}
                        </button>
                    )}
                    {zahl > start && (
                        <button type="button" className="mz-knopf leise" onClick={() => setZahl(start)}>
                            weniger anzeigen
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

/** „3 überfällig · 5 heute" — überfällig hervorgehoben. */
const FristSumme: React.FC<{ text: string }> = ({ text }) => (
    <>
        {text.split(" · ").map((teil, i) => (
            <React.Fragment key={i}>
                {i > 0 && " · "}
                <span className={teil.endsWith("überfällig") ? "ueber" : undefined}>{teil}</span>
            </React.Fragment>
        ))}
    </>
);

/* ────────── Zeile ────────── */

const Zeile: React.FC<{
    e: MzEintrag;
    jetzt: Date;
    touch: boolean;
    onOeffnen: (link: string) => void;
    onAbhaken: (id: number) => void;
    onMailGelesen: (m: PosteingangMail) => void;
    onMailGeoeffnet: (m: PosteingangMail) => void;
    onEinladungBeantwortet: (m: PosteingangMail) => void;
    onVorgangsMailGeoeffnet: (n: NotificationDto) => void;
    onAnrufErledigt: (a: VerpassterAnruf) => void;
}> = ({ e, jetzt, touch, onOeffnen, onAbhaken, onMailGelesen, onMailGeoeffnet, onEinladungBeantwortet, onVorgangsMailGeoeffnet, onAnrufErledigt }) => {
    if (e.art === "termin") {
        const m = e.mail;
        return (
            <MzTerminZeile
                m={m}
                touch={touch}
                onOeffnen={() => {
                    onOeffnen(mailDeepLink(m));
                    onMailGeoeffnet(m);
                }}
                onGelesen={() => onMailGelesen(m)}
                onErledigt={() => onMailGeoeffnet(m)}
                onBeantwortet={() => onEinladungBeantwortet(m)}
            />
        );
    }
    if (e.art === "mail") {
        const m = e.mail;
        const oeffnen = () => {
            onOeffnen(mailDeepLink(m));
            onMailGeoeffnet(m);
        };
        return (
            <div className="mz-zeile" role="button" tabIndex={0} onClick={oeffnen} onKeyDown={tasteWieKlick(oeffnen)} title={m.betreff}>
                <span className="mz-icon" aria-hidden>
                    <MailInbox20Regular />
                </span>
                <span className="mz-text">
                    <div className="mz-z1">
                        {m.absender} · „{m.betreff}"
                    </div>
                    {m.vorschau && <div className="mz-z2">{m.vorschau}</div>}
                </span>
                <span className="mz-zeit">{kurzZeit(m.empfangen, jetzt)}</span>
                <NotifAbhaken touch={touch} label="Als gelesen markieren" onAbhaken={() => onMailGelesen(m)} />
            </div>
        );
    }

    if (e.art === "anruf") {
        const a = e.anruf;
        const kopf = [a.nummer ?? "Unbekannte Nummer", a.kontaktName].filter(Boolean).join(" · ");
        // Ins Detail des (jüngsten) Anrufs in der Telefonie, nicht in den Kontakt.
        const oeffnen = () => onOeffnen(anrufDetailLink(a));
        return (
            <div className="mz-zeile" role="button" tabIndex={0} onClick={oeffnen} onKeyDown={tasteWieKlick(oeffnen)}>
                <span className="mz-icon" aria-hidden>
                    <Call20Regular />
                </span>
                <span className="mz-text">
                    <div className="mz-z1">{kopf}</div>
                    <div className="mz-z2">
                        {anrufZeit(a.zeit, jetzt)}
                        {a.anzahl > 1 ? ` · ${a.anzahl}× versucht` : ""}
                    </div>
                </span>
                <NotifAbhaken touch={touch} label="Anruf als erledigt markieren" onAbhaken={() => onAnrufErledigt(a)} />
            </div>
        );
    }

    const n = e.n;
    return (
        <MitteilungsZeile
            n={n}
            frist={e.frist}
            jetzt={jetzt}
            touch={touch}
            onOeffnen={onOeffnen}
            onAbhaken={() => onAbhaken(n.id)}
            onVorgangsMailGeoeffnet={onVorgangsMailGeoeffnet}
        />
    );
};

const MitteilungsZeile: React.FC<{
    n: NotificationDto;
    frist?: FristStufe;
    jetzt: Date;
    touch: boolean;
    onOeffnen: (link: string) => void;
    onAbhaken: () => void;
    onVorgangsMailGeoeffnet: (n: NotificationDto) => void;
}> = ({ n, frist, jetzt, touch, onOeffnen, onAbhaken, onVorgangsMailGeoeffnet }) => {
    const link = n.link ?? null;
    const istVorgangsMail = mitteilungsAbschnitt(n.type) === "mail_vorgang";
    const oeffnen = () => {
        if (!link) return;
        onOeffnen(link);
        // T-364 gilt weiter für alles andere; eine geöffnete Mail ist gelesen und erledigt.
        if (istVorgangsMail) onVorgangsMailGeoeffnet(n);
    };

    let z1: React.ReactNode;
    let z2: React.ReactNode;
    let z3: React.ReactNode = null;
    if (istVorgangsMail) {
        const t = vorgangsMailTeile(n);
        z1 = t.absender || t.betreff ? [t.absender, t.betreff ? `„${t.betreff}"` : null].filter(Boolean).join(" · ") : n.title;
        z2 = (
            <>
                {t.vorgang ? `Vorgang „${t.vorgang}"` : notifLabel(n.type)}
                {t.inhaber ? ` (${t.inhaber})` : ""}
                {t.rolle && (
                    <>
                        {" · "}
                        <span className="mz-rolle">{t.rolle}</span>
                    </>
                )}
                {n.kundeName ? ` · ${n.kundeName}` : ""}
            </>
        );
        // Hängt die Mail an einem To-do des Vorgangs: eine Zeile mehr.
        if (t.todo) z3 = `To-do „${t.todo}"`;
    } else {
        z1 = (
            <>
                {frist && <span className={`mz-stufe ${frist}`}>{STUFE_TEXT[frist]}</span>}
                {n.title}
            </>
        );
        z2 = mitteilungsZweiteZeile(n) ?? notifLabel(n.type);
    }

    return (
        <div
            className={`mz-zeile${link ? "" : " ohne-ziel"}`}
            role={link ? "button" : undefined}
            tabIndex={link ? 0 : undefined}
            onClick={oeffnen}
            onKeyDown={link ? tasteWieKlick(oeffnen) : undefined}
            title={notifLabel(n.type)}
        >
            <span className={`mz-icon${frist === "ueberfaellig" ? " ueber" : ""}`} aria-hidden>
                {notifIcon(n.type)}
            </span>
            <span className="mz-text">
                <div className="mz-z1">{z1}</div>
                <div className="mz-z2">{z2}</div>
                {z3 && <div className="mz-z2 mz-todo">{z3}</div>}
            </span>
            <span className="mz-zeit">{kurzZeit(n.createdAt, jetzt)}</span>
            <NotifAbhaken touch={touch} onAbhaken={onAbhaken} />
        </div>
    );
};

export default MitteilungszentrumListe;
